import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box,
  createTheme,
  ThemeProvider,
  IconButton,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { WorkSettings, AverageWorkSettings } from './types';
import OffTimeCalculator from './components/OffTimeCalculator';
import AverageWorkCalculator from './components/AverageWorkCalculator';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const theme = createTheme({
  palette: {
    primary: {
      main: '#558B6E',
    },
    background: {
      default: '#EDEDED',
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          borderRadius: '11px',
          '& .MuiOutlinedInput-root': {
            borderRadius: '11px',
          },
          '& .MuiOutlinedInput-input': {
            padding: '12px 16px',
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(0, 0, 0, 0.74)',
            fontSize: '14px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '11px',
          textTransform: 'none',
          height: '44px',
          fontSize: '16px',
          fontWeight: 500,
        },
      },
    },
  },
});

const STORAGE_KEY = 'workSettings';
const AVERAGE_WORK_STORAGE_KEY = 'averageWorkSettings';

const defaultSettings: WorkSettings = {
  minWorkHours: 8,
  lunchBreakStart: '12:00',
  lunchBreakEnd: '13:00',
  includeLunchBreak: false
};

const defaultAverageSettings: AverageWorkSettings = {
  expectedAverageHours: 8,
  overtimeWorkHours: 12,
  normalWorkHours: 9.5
};

function App() {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [settings, setSettings] = useState<WorkSettings>(defaultSettings);
  const [averageSettings, setAverageSettings] = useState<AverageWorkSettings>(defaultAverageSettings);

  useEffect(() => {
    const savedSettings = localStorage.getItem(STORAGE_KEY);
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
    
    const savedAverageSettings = localStorage.getItem(AVERAGE_WORK_STORAGE_KEY);
    if (savedAverageSettings) {
      setAverageSettings(JSON.parse(savedAverageSettings));
    }
  }, []);

  const saveSettings = (newSettings: WorkSettings) => {
    setSettings(newSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
  };

  const saveAverageSettings = (newSettings: AverageWorkSettings) => {
    setAverageSettings(newSettings);
    localStorage.setItem(AVERAGE_WORK_STORAGE_KEY, JSON.stringify(newSettings));
  };



  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 顶部状态栏 */}
        <Box sx={{
          height: '21px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          position: 'relative',
          zIndex: 1
        }}>
          <Typography variant="body2">12:00</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ width: '33px', height: '19px' }} />
            <Box sx={{ width: '33px', height: '19px' }} />
            <Box sx={{ width: '33px', height: '19px' }} />
          </Box>
        </Box>

        {/* 返回按钮 */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 67,
            left: 21,
            zIndex: 1
          }}
        >
          {/* <ArrowBackIcon /> */}
        </IconButton>

        {/* 圆形装饰 */}
        <Box
          sx={{
            position: 'absolute',
            top: -122,
            left: -107,
            width: '291px',
            height: '245px',
            borderRadius: '50%',
            bgcolor: 'primary.main',
            opacity: 0.44,
            zIndex: 0
          }}
        />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ pt: 8, pb: 4 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              align="center" 
              sx={{ 
                fontWeight: 'bold',
                mb: 2
              }}
            >
              工时管理工具
            </Typography>

            <Typography 
              variant="h6" 
              align="center" 
              color="primary"
              sx={{ 
                mb: 4,
                opacity: 0.97
              }}
            >
              智能计算下班时间和平均工时
            </Typography>

            {/* 标签页 */}
            <Paper sx={{ mb: 3, borderRadius: '11px' }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    borderRadius: '11px 11px 0 0',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 500,
                  }
                }}
              >
                <Tab label="下班时间计算" />
                <Tab label="平均工时计算" />
              </Tabs>
            </Paper>

            {/* 标签页内容 */}
            {activeTab === 0 && (
              <OffTimeCalculator 
                settings={settings}
                onSettingsChange={saveSettings}
              />
            )}
            
            {activeTab === 1 && (
              <AverageWorkCalculator
                settings={averageSettings}
                onSettingsChange={saveAverageSettings}
              />
            )}
          </Box>
        </Container>

        {/* 底部导航栏 */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '34px',
            bgcolor: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              width: '133px',
              height: '5px',
              bgcolor: 'black',
              borderRadius: '100px'
            }}
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
